import { useQuery } from '@tanstack/react-query';

import type { GetScanAndUploadFormsResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get scan and upload forms status from a purchased vehicle
 */
export const getScanAndUploadForms = async (
	servicesProviderId: string,
	orderId: string,
): Promise<GetScanAndUploadFormsResponse> => {
	const response = await apiClient.get<GetScanAndUploadFormsResponse>(
		ordersEndpoints.scanAndUploadForms(servicesProviderId, orderId),
	);
	return response.data;
};

/**
 * Hook for getting scan and upload forms status from a purchased vehicle
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetScanAndUploadForms = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '')._ctx.progress._ctx
			.scanAndUploadForms.queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error('Unable to get scan and upload forms status without an order ID.');
			}
			return getScanAndUploadForms(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
